import React, { useEffect, useState, useMemo, useCallback } from "react";
import { 
  Card, Row, Col, Statistic, Table, Tag, Progress, Typography, 
  Select, DatePicker, Button, Space, Modal, Checkbox, Tabs, 
  message, Input, InputNumber, List, Spin, Badge
} from "antd";
import { 
  DashboardOutlined, WarningOutlined, SearchOutlined, SafetyOutlined, 
  FallOutlined, DownloadOutlined, StopOutlined, 
  UserOutlined, RocketOutlined, AuditOutlined, 
  FilterOutlined, EnvironmentOutlined, BarChartOutlined,
  CheckCircleOutlined, PieChartOutlined
} from "@ant-design/icons";
import axiosInstance from "../../services/axiosServicesVisit";
import { useSelector, useDispatch } from "react-redux";
import { getDelegatesList } from "../../redux/slices/delegates/delegatesThunk";
import { getSamples } from "../../redux/slices/samples/samplesThunk";
import { getRegions } from "../../redux/slices/regions/regionsThunk";
import DoctorHistoryModal from '../../components/DoctorHistoryModal';
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const StocksDashboard = () => {
  const dispatch = useDispatch();
  
  // --- 1. ÉTATS DES DONNÉES ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recurringAlerts, setRecurringAlerts] = useState([]);
  const [recLoading, setRecLoading] = useState(false);
  const [geoData, setGeoData] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);

  
  
  // --- 2. ÉTATS MODALES ---
  const [modalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedRowInfo, setSelectedRowInfo] = useState("");
  const [doctorModalVisible, setDoctorModalVisible] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);

  const handleOpenDoctorDetails = (personId) => {
    setSelectedDoctorId(personId);
    setDoctorModalVisible(true);
  };
  const [pivotModalVisible, setPivotModalVisible] = useState(false);
  const [pivotData, setPivotData] = useState({ samples: [], products: [] });
  const [pivotLoading, setPivotLoading] = useState(false);
  const [selectedDelegateInfo, setSelectedDelegateInfo] = useState({ id: null, name: "" });
  const [geoDetailModalVisible, setGeoDetailModalVisible] = useState(false);
  const [geoDetailData, setGeoDetailData] = useState([]);
  const [geoDetailLoading, setGeoDetailLoading] = useState(false);
  const [selectedRegionName, setSelectedRegionName] = useState("");
  const [samplePivotVisible, setSamplePivotVisible] = useState(false);
  const [samplePivotData, setSamplePivotData] = useState(null);
  const [samplePivotLoading, setSamplePivotLoading] = useState(false);
  const [subDetailVisible, setSubDetailVisible] = useState(false);
  const [subDetailTitle, setSubDetailTitle] = useState("");
  const [subDetailData, setSubDetailData] = useState([]);
  const [subDetailLoading, setSubDetailLoading] = useState(false);

  // --- 3. ÉTATS DES FILTRES ---
  const [filters, setFilters] = useState({ delegateId: null, dates: [dayjs("2026-01-01"), dayjs()] });
  const [sampleFilter, setSampleFilter] = useState(null);
  const [regionFilter, setRegionFilter] = useState(null); 
  const [regionSearch, setRegionSearch] = useState("");   // Stocke ce que vous tapez
  const [showOnlyHorsCible, setShowOnlyHorsCible] = useState(false);
  const [showOnlyConsumed, setShowOnlyConsumed] = useState(false);
  const [showOnlySpecific, setShowOnlySpecific] = useState(false);
  const [showOnlyEmpty, setShowOnlyEmpty] = useState(false);
  const [modalDoctorSearch, setModalDoctorSearch] = useState("");
  const [modalSpecialtyFilter, setModalSpecialtyFilter] = useState(null);
  const [journeyDelegateFilter, setJourneyDelegateFilter] = useState(null);
  const [journeySampleFilter, setJourneySampleFilter] = useState(null);
  const [journeyTargetingFilter, setJourneyTargetingFilter] = useState(null);
  const [recFilters, setRecFilters] = useState({ delegateId: null, doctorSearch: "", minStreak: 2 });

  const delegatesList = useSelector((state) => state.delegates.delegatesList?.data?.data || []);
  const samplesList = useSelector((state) => state.samples.samples?.data || []);
  const regionsList = useSelector((state) => state.regions.regions?.data || []);

  // --- 4. FONCTIONS ACTIONS ---



  const handleOpenDelegatePivot = async (delegateId, delegateName, personId = null) => {
    setPivotModalVisible(true);
    setPivotLoading(true);
    setSelectedDelegateInfo({ id: delegateId, name: delegateName });
    
    try {
      const params = { 
        startDate: filters.dates[0].format('DD-MM-YYYY'), 
        endDate: filters.dates[1].format('DD-MM-YYYY'),
        // On n'envoie le personId que s'il existe
        ...(personId && { personId }) 
      };
      
      // Nouvelle URL sans le /doctor/undefined
      const res = await axiosInstance.get(`/v1/delegate-stocks/pivot/delegate/${delegateId}`, { params });
      setPivotData(res.data);
    } catch (e) { 
      message.error("Erreur lors du chargement de l'analyse croisée"); 
    } finally { 
      setPivotLoading(false); 
    }
  };

  const handleOpenNonCompliantDetails = async (record, type) => {
    const labelTitle = type === 'hors-cible' ? "Détails Hors Cible (Ciblage)" : "Détails Demandes Spécifiques";
    setSelectedRowInfo(`${record.delegate} - ${record.sample} (${labelTitle})`);
    setModalDoctorSearch(""); setModalSpecialtyFilter(null);
    setModalVisible(true); setModalLoading(true);
    try {
      const params = { 
        delegateId: record.delegateId, sampleId: record.sampleId, 
        startDate: filters.dates[0].format('DD-MM-YYYY'), endDate: filters.dates[1].format('DD-MM-YYYY'),
        type: type 
      };
      const res = await axiosInstance.get("/v1/delegate-stocks/analytics/non-compliant-details", { params });
      setModalData(res.data || []);
    } catch { message.error("Erreur détails"); }
    finally { setModalLoading(false); }
  };

  const handleOpenGeoDetail = async (record) => {
    setSelectedRegionName(record.regionName);
    setGeoDetailModalVisible(true); setGeoDetailLoading(true);
    try {
      const params = { 
        regionId: record.regionId, sampleId: sampleFilter || undefined, 
        delegateId: filters.delegateId || undefined,
        startDate: filters.dates[0].format('DD-MM-YYYY'), endDate: filters.dates[1].format('DD-MM-YYYY') 
      };
      const res = await axiosInstance.get("/v1/delegate-stocks/analytics/geo-distribution-detail", { params });
      setGeoDetailData(res.data || []);
    } catch { message.error("Erreur région"); }
    finally { setGeoDetailLoading(false); }
  };

  const handleOpenSamplePivot = async (sampleId, sampleName) => {
    setSamplePivotVisible(true); 
    setSamplePivotLoading(true);
    try {
      // Préparation des paramètres de date
      const params = { 
        startDate: filters.dates[0].format('DD-MM-YYYY'), 
        endDate: filters.dates[1].format('DD-MM-YYYY') 
      };

      // --- CORRECTIF : Appel de la route GLOBALE qui ignore le filtre délégué ---
      const resGlobal = await axiosInstance.get(`/v1/delegate-stocks/analytics/sample-global-summary/${sampleId}`, { params });
      const stats = resGlobal.data;

      setSamplePivotData({
        sampleId, 
        name: sampleName, 
        totalQuantity: stats.totalVolume,
        delegateCount: stats.delegateCount, 
        doctorCount: stats.doctorCount
      });
    } catch { 
      message.error("Erreur lors du calcul des statistiques globales"); 
    } finally { 
      setSamplePivotLoading(false); 
    }
  };

  const handleOpenSubDetail = async (type, sampleId, sampleName) => {
    setSubDetailVisible(true);
    setSubDetailLoading(true);
    setSubDetailData([]);

    // --- FIX : On force delegateId à undefined pour voir TOUT LE MONDE ---
    const params = { 
        sampleId, 
        delegateId: undefined, // <--- AJOUTÉ : Ignore le filtre global
        startDate: filters.dates[0].format('DD-MM-YYYY'), 
        endDate: filters.dates[1].format('DD-MM-YYYY') 
    };

    try {
      // 1. On récupère les régions qui ont distribué cet échantillon
      const resGeo = await axiosInstance.get("/v1/delegate-stocks/analytics/geo-potential", { params });
      const regionsActive = resGeo.data || [];

      if (type === 'regions') {
        setSubDetailData(regionsActive);
        setSubDetailTitle(`Régions couvertes par : ${sampleName}`);
      } else {
        // 2. On récupère les lignes de rapports pour agréger les délégués ou médecins
        const detailRequests = regionsActive.map(r => 
            axiosInstance.get("/v1/delegate-stocks/analytics/geo-distribution-detail", { 
                params: { ...params, regionId: r.regionId } 
            })
        );
        const allRes = await Promise.all(detailRequests);
        const allLines = allRes.flatMap(res => res.data || []);

        if (type === 'doctors') {
          const uniqueDocs = []; const seen = new Set();
          allLines.forEach(d => { if (!seen.has(d.personId)) { seen.add(d.personId); uniqueDocs.push(d); } });
          setSubDetailData(uniqueDocs);
          setSubDetailTitle(`Médecins ayant reçu : ${sampleName}`);
        } else if (type === 'delegates') {
          const uniqueDelegates = []; const seen = new Set();
          allLines.forEach(d => {
            if (!seen.has(d.delegateId)) { 
                seen.add(d.delegateId); 
                // Somme des volumes pour ce délégué précis
                const totalForDel = allLines
                    .filter(l => l.delegateId === d.delegateId)
                    .reduce((a, b) => a + Number(b.quantity), 0);
                uniqueDelegates.push({ ...d, totalQuantity: totalForDel }); 
            }
          });
          setSubDetailData(uniqueDelegates);
          setSubDetailTitle(`Délégués ayant distribué : ${sampleName}`);
        }
      }
    } catch { message.error("Erreur de chargement"); }
    finally { setSubDetailLoading(false); }
  };

  // --- 5. LOGIQUE API & EXPORTS ---

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = { delegateId: filters.delegateId || undefined, startDate: filters.dates[0].format('DD-MM-YYYY'), endDate: filters.dates[1].format('DD-MM-YYYY') };
      const res = await axiosInstance.get("/v1/delegate-stocks/analytics", { params });
      setData(res.data || []);
    } catch (e) { message.error("Erreur analyses"); }
    finally { setLoading(false); }
  }, [filters]);

  const fetchGeoAnalysis = useCallback(async () => {
    setGeoLoading(true);
    try {
      const params = { sampleId: sampleFilter || undefined, delegateId: filters.delegateId || undefined, regionId: regionFilter || undefined, startDate: filters.dates[0].format('DD-MM-YYYY'), endDate: filters.dates[1].format('DD-MM-YYYY') };
      const res = await axiosInstance.get("/v1/delegate-stocks/analytics/geo-potential", { params });
      setGeoData(res.data || []);
    } catch (e) { console.error("Erreur géo"); }
    finally { setGeoLoading(false); }
  }, [filters, sampleFilter, regionFilter]);

  const fetchRecurringAlerts = useCallback(async () => {
    setRecLoading(true);
    try {
      const params = { ...recFilters, startDate: filters.dates[0].format('DD-MM-YYYY'), endDate: filters.dates[1].format('DD-MM-YYYY') };
      const res = await axiosInstance.get("/v1/delegate-stocks/analytics/recurring-non-compliance", { params });
      setRecurringAlerts(res.data || []);
    } catch (e) { console.error("Erreur alertes"); }
    finally { setRecLoading(false); }
  }, [filters, recFilters]);

  const handleExportPivot = async () => {
    const currentPersonId = selectedDoctorId; // 👈 REMPLACÉ ICI (au lieu de doctorDetailData[0]?.personId)
    if (!selectedDelegateInfo.id || !currentPersonId) return;
    try {
      const params = { startDate: filters.dates[0].format('DD-MM-YYYY'), endDate: filters.dates[1].format('DD-MM-YYYY') };
      const response = await axiosInstance.get(`/v1/delegate-stocks/pivot/delegate/${selectedDelegateInfo.id}/doctor/${currentPersonId}/export/csv`, { params, responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.download = `Analyse_Croisee.csv`; link.click();
    } catch { message.error("Erreur export"); }
  };

  const handleExportSampleAnalysis = async () => {
    if (!samplePivotData) return;
    const hide = message.loading("Export en cours...", 0);
    try {
      const params = { sampleId: samplePivotData.sampleId, startDate: filters.dates[0].format('DD-MM-YYYY'), endDate: filters.dates[1].format('DD-MM-YYYY') };
      const resGeo = await axiosInstance.get("/v1/delegate-stocks/analytics/geo-potential", { params });
      const allRequests = (resGeo.data || []).map(r => axiosInstance.get("/v1/delegate-stocks/analytics/geo-distribution-detail", { params: { ...params, regionId: r.regionId } }));
      const allRes = await Promise.all(allRequests);
      const rawLines = allRes.flatMap(res => res.data || []);
      const headers = ["Date", "Medecin", "Specialite", "Region", "Delegue", "Quantite"];
      const rows = rawLines.map(item => [dayjs(item.reportDate).format('DD/MM/YYYY'), item.doctorName, item.specialtyName || "-", item.regionName || "-", item.delegateName, item.quantity]);
      const csvContent = "\ufeff" + [headers, ...rows].map(e => e.join(";")).join("\n");
      const link = document.createElement("a"); link.href = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }));
      link.download = `Analyse_Produit.csv`; link.click();
      hide(); message.success("Export réussi");
    } catch { hide(); message.error("Erreur export"); }
  };

  const handleExportSubDetail = () => {
    if (!subDetailData || subDetailData.length === 0) return;
    let headers = [], rows = [];
    if (subDetailTitle.includes("Médecins")) {
      headers = ["Nom Medecin", "Specialite", "Region"];
      rows = subDetailData.map(r => [r.doctorName, r.specialtyName || "-", r.regionName || "-"]);
    } else if (subDetailTitle.includes("Délégués")) {
      headers = ["Nom Delegue", "Volume"];
      rows = subDetailData.map(r => [r.delegateName, r.totalQuantity || 0]);
    } else if (subDetailTitle.includes("Régions")) {
      headers = ["Nom de la Region", "Volume"];
      rows = subDetailData.map(r => [r.regionName, r.totalQuantity || 0]);
    }
    const csvContent = "\ufeff" + [headers, ...rows].map(e => e.join(";")).join("\n");
    const link = document.createElement("a"); link.href = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }));
    link.download = `Export_Liste.csv`; link.click();
  };

  const refreshAll = () => { fetchAnalytics(); fetchGeoAnalysis(); fetchRecurringAlerts(); };

  useEffect(() => {
    dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
    dispatch(getSamples({ page: 1, limit: 1000 }));
    dispatch(getRegions({ page: 1, itemsPerPage: 1000 }));
  }, [dispatch]);

  useEffect(() => { refreshAll(); }, [fetchAnalytics, fetchGeoAnalysis, fetchRecurringAlerts]);

  // --- 6. MÉMOS FILTRAGE ---

  const filteredData = useMemo(() => {
    return data.filter(item => {
      const matchSample = sampleFilter ? item.sampleId === sampleFilter : true;
      const matchHorsCible = showOnlyHorsCible ? item.consumedHorsCible > 0 : true;
      const matchConsumed = showOnlyConsumed ? item.consumed > 0 : true;
      const matchSpecific = showOnlySpecific ? item.consumedSpecific > 0 : true;
      const matchEmpty = showOnlyEmpty ? item.remaining === 0 : true;
      return matchSample && matchHorsCible && matchConsumed && matchSpecific && matchEmpty;
    });
  }, [data, sampleFilter, showOnlyHorsCible, showOnlyConsumed, showOnlySpecific, showOnlyEmpty]);

  const filteredModalData = useMemo(() => {
    return modalData.filter(item => {
      const matchDoctor = item.doctorName?.toLowerCase().includes(modalDoctorSearch.toLowerCase());
      const matchSpecialty = modalSpecialtyFilter ? item.specialtyName === modalSpecialtyFilter : true;
      return matchDoctor && matchSpecialty;
    });
  }, [modalData, modalDoctorSearch, modalSpecialtyFilter]);



  const filteredGeoData = useMemo(() => {
    return geoData.filter(item => {
      // 1. Si une région est sélectionnée (ID présent), on ne montre que celle-là
      if (regionFilter) {
        return item.regionId === regionFilter;
      }
      // 2. Sinon, on compare le nom de la région avec le texte tapé (regionSearch)
      return item.regionName?.toLowerCase().includes(regionSearch.toLowerCase());
    });
}, [geoData, regionSearch, regionFilter]);

  const modalSpecialties = useMemo(() => [...new Set(modalData.map(i => i.specialtyName))].filter(Boolean), [modalData]);

  // --- 7. COLONNES TABLEAUX ---
  const mainColumns = [
    { title: "Délégué", dataIndex: "delegate" },
    { title: "Échantillon", dataIndex: "sample", render: (t, r) => <span onClick={() => handleOpenSamplePivot(r.sampleId, t)} style={{fontWeight:'bold', color:'#1890ff', cursor:'pointer', textDecoration:'underline'}}>{t}</span> },
    { title: "Ciblé", dataIndex: "consumedTarget", align: 'center', render: v => <Text strong style={{ color: '#52c41a' }}>{v}</Text> },
    { title: "Hors Cible", dataIndex: "consumedHorsCible", align: 'center', render: (v, r) => <Tag color={v > 0 ? "volcano" : "green"} style={{cursor:'pointer', fontWeight:'bold'}} onClick={() => handleOpenNonCompliantDetails(r, 'hors-cible')}>{v}</Tag> },
    { title: "Dde Spécifique", dataIndex: "consumedSpecific", align: 'center', render: (v, r) => <Tag color={v > 0 ? "purple" : "default"} style={{cursor:'pointer', fontWeight:'bold'}} onClick={() => handleOpenNonCompliantDetails(r, 'specific')}>{v}</Tag> },
    { title: "Autonomie", dataIndex: "prediction", render: d => <Tag color={d < 7 ? "red" : "blue"}>{d >= 999 ? "Stable" : `${d} j`}</Tag> },
    { title: "Stock", render: (_, r) => <Progress percent={Math.round((r.remaining / (r.initial || 1)) * 100) || 0} size="small" status={r.isCritical ? "exception" : "active"} /> }
  ];

  return (
    <div className="layout-content">
      <Title level={3}><DashboardOutlined /> Tableau de Bord Analytique des Stocks</Title>

      <Card style={{ marginBottom: 24 }} title="Filtres Analyse Globale" className="circlebox">
        <Space size="large" wrap>
          <Select showSearch placeholder="Délégué..." style={{ width: 250 }} onChange={(val) => setFilters({...filters, delegateId: val})} allowClear optionFilterProp="children">{delegatesList.map(d => <Select.Option key={d.id} value={d.id}>{d.firstname} {d.lastname}</Select.Option>)}</Select>
          <RangePicker format="DD-MM-YYYY" value={filters.dates} onChange={(val) => setFilters({...filters, dates: val})} />
          <Button type="primary" icon={<SearchOutlined />} onClick={refreshAll}>Actualiser</Button>
        </Space>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={6}><Card bordered={false} className="circlebox"><Statistic title="Total Hors Cible" value={data.reduce((a, b) => a + (b.consumedHorsCible || 0), 0)} valueStyle={{ color: '#fa8c16' }} prefix={<WarningOutlined />} /></Card></Col>
        <Col xs={24} md={6}><Card bordered={false} className="circlebox"><Statistic title="Total Dde Spécifique" value={data.reduce((a, b) => a + (b.consumedSpecific || 0), 0)} valueStyle={{ color: '#722ed1' }} prefix={<RocketOutlined />} /></Card></Col>
        <Col xs={24} md={6}><Card bordered={false} className="circlebox"><Statistic title="Respect Ciblage" value={data.reduce((a,b) => a + b.consumed, 0) > 0 ? (1 - (data.reduce((a,b) => a + b.consumedHorsCible, 0) / data.reduce((a,b) => a + b.consumed, 0))) * 100 : 100} precision={1} suffix="%" prefix={<SafetyOutlined />} valueStyle={{ color: '#3f8600' }} /></Card></Col>
        <Col xs={24} md={6}><Card bordered={false} className="circlebox"><Statistic title="Volume Total Sortie" value={data.reduce((a,b) => a + b.consumed, 0)} prefix={<FallOutlined />} /></Card></Col>
      </Row>

      <Card title={<span><FilterOutlined /> Affinement des résultats</span>} bordered={false} className="circlebox" style={{ marginBottom: 24, backgroundColor: '#fafafa' }}>
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} md={8}><Text type="secondary" style={{ display: 'block', marginBottom: 5 }}>Échantillon :</Text><Select showSearch placeholder="Tous" style={{ width: '100%' }} allowClear onChange={(v) => setSampleFilter(v)}>{samplesList.map(s => <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>)}</Select></Col>
          <Col xs={24} md={16}><Text type="secondary" style={{ display: 'block', marginBottom: 10 }}>Filtres rapides :</Text><Space size="large" wrap>
            <Checkbox checked={showOnlyHorsCible} onChange={e => setShowOnlyHorsCible(e.target.checked)}>Hors Cible {'>'} 0</Checkbox>
            <Checkbox checked={showOnlySpecific} onChange={e => setShowOnlySpecific(e.target.checked)}>Dde. Spécifique {'>'} 0</Checkbox>
            <Checkbox checked={showOnlyConsumed} onChange={e => setShowOnlyConsumed(e.target.checked)}>Consommé {'>'} 0</Checkbox>
            <Checkbox checked={showOnlyEmpty} onChange={e => setShowOnlyEmpty(e.target.checked)}>Stock Épuisé (0%)</Checkbox>
          </Space></Col>
        </Row>
      </Card>

      <Card title="État détaillé par échantillon" bordered={false} className="circlebox" style={{ marginBottom: 24 }}>
        <Table columns={mainColumns} dataSource={filteredData} loading={loading} rowKey="id" pagination={{ pageSize: 10 }} />
      </Card>

      <Card title={<span><EnvironmentOutlined /> Analyse Géo-Stratégique : Volume par Région</span>} className="circlebox" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 15 }}>
          <Space>
            <Select
                showSearch               // Permet d'écrire dans le champ
                placeholder="Tapez ou choisissez une région..."
                style={{ width: 300 }}
                allowClear               // Affiche la petite croix pour effacer
                value={regionFilter}
                
                // 1. Capture le texte pendant la frappe (ex: vous tapez "sfax")
                onSearch={(val) => setRegionSearch(val)} 
                
                // 2. Capture la sélection finale dans la liste
                onChange={(val) => {
                    setRegionFilter(val);
                    setRegionSearch(""); // On vide la recherche textuelle une fois le choix fait
                }}
                
                // 3. Filtre les propositions à l'intérieur du menu déroulant
                filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
            >
                {regionsList.map(r => (
                    <Select.Option key={r.id} value={r.id}>
                        {r.name}
                    </Select.Option>
                ))}
            </Select>
          </Space>
        </div>
        <Table dataSource={geoData} loading={geoLoading} pagination={{ pageSize: 5 }} size="small" columns={[
            { title: 'Région', dataIndex: 'regionName', render: t => <Text strong>{t?.toUpperCase()}</Text> },
            { title: 'Volume Distribué', dataIndex: 'totalQuantity', render: (val, record) => (<span onClick={() => handleOpenGeoDetail(record)} style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 'bold', textDecoration: 'underline' }}>{val} unités</span>) },
            { title: 'Médecins touchés', dataIndex: 'doctorCount', align: 'center' },
            { title: 'Ratio', render: (_, r) => <Tag color="geekblue">{(r.totalQuantity / r.doctorCount).toFixed(1)} ech/doc</Tag> }
        ]} />
      </Card>

      <Card title={<span><StopOutlined /> Alertes de Récidive : Analyse Comportementale</span>} className="circlebox">
        <div style={{ marginBottom: 20, background: '#fff1f0', padding: '15px', borderRadius: 8, border: '1px solid #ffa39e' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col md={8}><Text strong style={{display:'block', marginBottom:5}}>Délégué :</Text><Select showSearch placeholder="Tous les délégués" style={{ width: '100%' }} allowClear onChange={v => setRecFilters({...recFilters, delegateId: v})}>{delegatesList.map(d => <Select.Option key={d.id} value={d.id}>{d.firstname} {d.lastname}</Select.Option>)}</Select></Col>
            <Col md={8}><Text strong style={{display:'block', marginBottom:5}}>Médecin :</Text><Input placeholder="Nom du médecin..." prefix={<UserOutlined />} value={recFilters.doctorSearch} onChange={e => setRecFilters({...recFilters, doctorSearch: e.target.value})} /></Col>
            <Col md={4}><Text strong style={{display:'block', marginBottom:5}}>Seuil :</Text><InputNumber min={1} style={{width:'100%'}} value={recFilters.minStreak} onChange={v => setRecFilters({...recFilters, minStreak: v})} /></Col>
            <Col md={4}><Button type="primary" danger block style={{marginTop:20}} onClick={fetchRecurringAlerts} icon={<SearchOutlined />}>Analyser</Button></Col>
          </Row>
        </div>
        <Table dataSource={recurringAlerts} loading={recLoading} size="small" columns={[
          { title: "Délégué", dataIndex: "delegateName", render: t => <Text strong>{t}</Text> },
          { title: "Médecin Visité", render: (_, r) => (<span onClick={() => handleOpenDoctorDetails(r.personId)} style={{ color: '#1890ff', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}>{r.doctorName}</span>) },
          { title: "Série Hors-Cible", dataIndex: "fautesSuccessives", align: 'center', render: c => <Tag color="red" style={{fontWeight:'bold'}}>{c} visites consécutives</Tag> },
          { title: "Dernières dates", dataIndex: "lastVisitDates", render: dates => <div style={{ fontSize: '11px' }}>{dates?.slice(0, 3).map((d, i) => <div key={i}>• {dayjs(d).format('DD/MM/YYYY')}</div>)}</div> }
        ]} />
      </Card>

      {/* --- TOUTES LES MODALES --- */}

      <Modal zIndex={1000} title={<span>{selectedRowInfo}</span>} open={modalVisible} onCancel={() => setModalVisible(false)} footer={null} width={900}>
        <div style={{ marginBottom: 16, background: '#f5f5f5', padding: 12, borderRadius:8 }}>
          <Row gutter={16}><Col span={24}><Input placeholder="Rechercher..." prefix={<SearchOutlined />} value={modalDoctorSearch} onChange={e => setModalDoctorSearch(e.target.value)} allowClear /></Col></Row>
        </div>
        <Table dataSource={filteredModalData} loading={modalLoading} rowKey="reportId" size="small" columns={[
            { title: "Date", render: r => dayjs(r.reportDate).format('DD/MM/YYYY HH:mm') }, 
            { 
              title: "Médecin", 
              render: r => (
                <Space direction="vertical" size={0}>
                  <span 
                    onClick={() => handleOpenDoctorDetails(r.personId)} 
                    style={{ fontWeight: 'bold', color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {r.doctorName}
                  </span>
                  {r.validationStatus && (
                    <Tag 
                      // ROUGE si contient "NON" ou "HORS", VERT sinon
                      color={
                        r.validationStatus.toUpperCase().includes('NON') || 
                        r.validationStatus.toUpperCase().includes('HORS') 
                        ? 'red' : 'green'
                      } 
                      style={{ fontSize: '10px', marginTop: '2px', fontWeight: 'bold' }}
                    >
                      {r.validationStatus.toUpperCase()}
                    </Tag>
                  )}
                </Space>
              )
            },
            { title: "Spécialité", dataIndex: "specialtyName" }, { title: "Qté", dataIndex: "quantity", align: 'center' }
        ]} />
      </Modal>

      <Modal zIndex={1000} title={<div style={{display:'flex', justifyContent:'space-between', alignItems:'center', width:'95%'}}><span><PieChartOutlined /> Analyse Croisée Produit : {samplePivotData?.name}</span><Button type="primary" size="small" icon={<DownloadOutlined />} onClick={handleExportSampleAnalysis}>Exporter</Button></div>} open={samplePivotVisible} onCancel={() => setSamplePivotVisible(false)} footer={null} width={650}>
        <Spin spinning={samplePivotLoading}>
          <Table pagination={false} showHeader={false} dataSource={[{ key: '1', label: 'Quantité totale sortie', value: `${samplePivotData?.totalQuantity || 0} unités`, icon: <FallOutlined />, color: 'blue' },{ key: '2', label: 'Médecins touchés', value: `${samplePivotData?.doctorCount || 0} médecins`, icon: <UserOutlined />, color: 'cyan' },{ key: '3', label: 'Délégués impliqués', value: `${samplePivotData?.delegateCount || 0} délégués`, icon: <RocketOutlined />, color: 'geekblue' }]} columns={[{ dataIndex: 'icon', width: 50, render: i => <span style={{fontSize:20, color:'#1890ff'}}>{i}</span> }, { dataIndex: 'label', render: t => <Text strong style={{fontSize:15}}>{t}</Text> }, { dataIndex: 'value', align: 'right', render: (v, r) => (<Tag color={r.color} style={{fontSize:14, padding:'4px 12px', cursor:'pointer', fontWeight:'bold'}} onClick={() => {if (r.key === '2') handleOpenSubDetail('doctors', samplePivotData.sampleId, samplePivotData.name);if (r.key === '3') handleOpenSubDetail('delegates', samplePivotData.sampleId, samplePivotData.name);}}>{v}</Tag>)}] } />
        </Spin>
      </Modal>

      <Modal 
          zIndex={1100} 
          title={<div style={{display:'flex', justifyContent:'space-between', alignItems:'center', width:'95%'}}><span><SearchOutlined /> {subDetailTitle}</span><Button size="small" icon={<DownloadOutlined />} onClick={handleExportSubDetail}>Exporter cette liste</Button></div>} 
          open={subDetailVisible} 
          onCancel={() => setSubDetailVisible(false)} 
          footer={null} 
          width={1000} 
        >
          <Table 
            dataSource={subDetailData} 
            loading={subDetailLoading} 
            size="small" 
            pagination={{ pageSize: 10 }} 
            rowKey={(r, i) => i} 
            columns={[
              { 
                // TITRE DE LA COLONNE DYNAMIQUE
                title: subDetailTitle.includes("Médecins") ? 'Médecin' : (subDetailTitle.includes("Délégués") ? 'Délégué' : 'Région'), 
                render: (_, r) => {
                  // AFFICHAGE STRICT DU NOM SELON LE CONTEXTE
                  if (subDetailTitle.includes("Médecins")) return <Text strong>{r.doctorName}</Text>;
                  if (subDetailTitle.includes("Délégués")) return <Text strong>{r.delegateName}</Text>;
                  return <Text strong>{r.regionName}</Text>;
                } 
              },
              { 
                title: 'Secteur / Spécialité', 
                render: (_, r) => {
                  if (subDetailTitle.includes("Médecins")) return `${r.regionName || 'N/A'} (${r.specialtyName || '-'})`;
                  if (subDetailTitle.includes("Délégués")) return r.regionName || "Secteur Assigné";
                  return "Secteur Géographique";
                }
              },
              { 
                title: 'Volume', 
                align: 'center', 
                render: (_, r) => {
                  const val = r.totalQuantity || r.quantity || 0;
                  return <Badge count={`${val} unités`} style={{ backgroundColor: val > 0 ? '#52c41a' : '#d9d9d9' }} />;
                } 
              },
              { 
                title: 'Action', 
                align: 'center', 
                render: (_, r) => {
                  // Action Parcours pour les médecins
                  if (subDetailTitle.includes("Médecins") && r.personId) {
                    return <Button size="small" type="primary" ghost icon={<AuditOutlined />} onClick={() => handleOpenDoctorDetails(r.personId)}>Parcours</Button>;
                  }
                  // Action Détails pour les délégués
                  if (subDetailTitle.includes("Délégués") && r.delegateId) {
                    return (
                      <Button 
                        size="small" 
                        type="primary" 
                        ghost 
                        onClick={() => handleOpenDelegatePivot(r.delegateId, r.delegateName)} // Plus de personId forcé
                      >
                        Détails
                      </Button>
                    );
                  }
                  return null;
                } 
              }
            ]} 
          />
        </Modal>

      <DoctorHistoryModal
        visible={doctorModalVisible}
        onClose={() => setDoctorModalVisible(false)}
        personId={selectedDoctorId}
        zIndex={1200}
      />

      <Modal zIndex={1300} title={<div style={{display:'flex', justifyContent:'space-between', alignItems:'center', width:'95%'}}><span><BarChartOutlined /> Analyse Croisée : {selectedDelegateInfo.name}</span><Button type="primary" size="small" icon={<DownloadOutlined />} onClick={handleExportPivot}>Exporter CSV</Button></div>} open={pivotModalVisible} onCancel={() => setPivotModalVisible(false)} footer={null} width={900}>
        <Spin spinning={pivotLoading}><Tabs items={[{ key: '1', label: 'Échantillons', children: <Table size="small" dataSource={pivotData.samples} pagination={false} columns={[{title:'Nom', dataIndex:'name'}, {title:'Total', dataIndex:'totalQuantity', align:'center'}]} /> }, { key: '2', label: 'Produits', children: <Table size="small" dataSource={pivotData.products} pagination={false} columns={[{title:'Nom', dataIndex:'name'}, {title:'Présentations', dataIndex:'visitCount', align:'center'}]} /> }]} /></Spin>
      </Modal>

      <Modal zIndex={1000} title={<span><EnvironmentOutlined /> Détails Distribution : {selectedRegionName?.toUpperCase()}</span>} open={geoDetailModalVisible} onCancel={() => setGeoDetailModalVisible(false)} footer={null} width={900}>
        <Table dataSource={geoDetailData} loading={geoDetailLoading} size="small" columns={[{ title: 'Date', dataIndex: 'reportDate', render: d => dayjs(d).format('DD/MM/YYYY') }, { title: 'Médecin', render: (_, r) => <span onClick={() => handleOpenDoctorDetails(r.personId)} style={{color:'#1890ff', cursor:'pointer', textDecoration:'underline', fontWeight:'bold'}}>{r.doctorName}</span> }, { title: 'Échantillon', dataIndex: 'sampleName' }, { title: 'Qté', dataIndex: 'quantity', align: 'center' }, { title: 'Délégué', dataIndex: 'delegateName' }]} />
      </Modal>
    </div>
  );
};

export default StocksDashboard;